<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useConnectionStore } from '@/stores/connection'

// Friendly labels for the Twilio Voice SDK's call quality warning names.
// Anything unmapped falls back to the raw name so new SDK warnings still show.
const WARNING_LABELS: Record<string, string> = {
  'high-rtt': 'High network latency',
  'high-jitter': 'Unstable network connection',
  'high-packet-loss': 'Network packet loss',
  'high-packets-lost-fraction': 'Network packet loss',
  'low-mos': 'Poor call quality',
  'low-bytes-received': 'No audio being received',
  'low-bytes-sent': 'No audio being sent',
  'constant-audio-input-level': 'Your microphone appears silent',
  'constant-audio-output-level': 'No audio from the other side',
  'ice-connectivity-lost': 'Media connection lost',
}

const { isReconnecting, callWarnings } = storeToRefs(useConnectionStore())

const warningText = computed(() => {
  const labels = callWarnings.value.map((name) => WARNING_LABELS[name] ?? name)
  return [...new Set(labels)].join(' · ')
})

const banner =
  'mx-[30px] mb-[10px] px-[12px] py-[6px] rounded-[4px] text-[13px] text-center text-white'
</script>

<template>
  <div v-if="isReconnecting" :class="banner" class="bg-[#dc143c]" data-e2e="reconnecting-banner">
    Connection lost — reconnecting&hellip;
  </div>
  <div
    v-else-if="callWarnings.length"
    :class="banner"
    class="bg-[#e6a23c]"
    data-e2e="call-warnings-banner"
  >
    {{ warningText }}
  </div>
</template>
